import { useState, type JSX } from 'react';
import { leadsPageSchema } from '@cryptology-assist/domain';
import { botPath, downloadCsv } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, useMutation } from './components';
import { dateLabel, statusLabel } from './copy';
import type { SettingsProps } from './settings';

export function Leads({ scope, copy, language }: SettingsProps): JSX.Element {
  const [from, setFrom] = useState('');
  const [through, setThrough] = useState('');
  const [before, setBefore] = useState<string | null>(null);
  const parameters = new URLSearchParams();
  if (from) parameters.set('from', from);
  if (through) parameters.set('through', through);
  const exportPath = `${botPath(scope, 'leads/export')}?${parameters}`;
  if (before) parameters.set('before', before);
  const page = useRemote(
    `${botPath(scope, 'leads')}?${parameters}`,
    leadsPageSchema
  );
  const mutation = useMutation();
  async function download() {
    const href = URL.createObjectURL(await downloadCsv(exportPath));
    const link = document.createElement('a');
    link.href = href;
    link.download = 'assist-leads.csv';
    link.click();
    URL.revokeObjectURL(href);
  }
  return (
    <div className="settings-content">
      <div className="section-heading">
        <h1>{copy.leads}</h1>
        <p>{copy.leadsHelp}</p>
      </div>
      <div className="form-grid">
        <Field label={copy.leadFrom}>
          <input
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setBefore(null);
            }}
          />
        </Field>
        <Field label={copy.leadThrough}>
          <input
            type="date"
            value={through}
            onChange={(event) => {
              setThrough(event.target.value);
              setBefore(null);
            }}
          />
        </Field>
      </div>
      <button
        disabled={mutation.busy || !page.data}
        onClick={() => void mutation.run(download)}
      >
        {copy.leadExport}
      </button>
      <p className="small muted">{copy.leadExportHelp}</p>
      <ErrorNotice
        error={page.error ?? mutation.error}
        copy={copy}
        onRetry={page.reload}
      />
      {page.loading && !page.data && <p role="status">{copy.loading}</p>}
      {page.data &&
        (page.data.leads.length === 0 ? (
          <p>{copy.leadNoData}</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{copy.leadCustomer}</th>
                  <th>{copy.leadContact}</th>
                  <th>{copy.leadRequest}</th>
                  <th>{copy.leadResult}</th>
                </tr>
              </thead>
              <tbody>
                {page.data.leads.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <strong>
                        {lead.firstName} {lead.lastName}
                      </strong>
                      <p>{dateLabel(lead.confirmedAt, language)}</p>
                      <small>{statusLabel(copy, lead.channel)}</small>
                    </td>
                    <td>
                      <p>{lead.phone}</p>
                      <p>{lead.email}</p>
                    </td>
                    <td className="lead-request">{lead.request}</td>
                    <td>
                      {statusLabel(copy, lead.status)}
                      {lead.acknowledgement && (
                        <p>{statusLabel(copy, lead.acknowledgement)}</p>
                      )}
                      {lead.reference && <small>{lead.reference}</small>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {before && (
        <button onClick={() => setBefore(null)}>{copy.leadFirst}</button>
      )}
      {page.data?.nextCursor && (
        <button onClick={() => setBefore(page.data!.nextCursor)}>
          {copy.leadNext}
        </button>
      )}
    </div>
  );
}
